"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ImageOff, Loader2, Search, Upload } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import uploadFile, { listImages, readSharingDefaults, type ImageRecord } from "../api/file";
import { resolveLabwickImageUrls, resolveLabwickUploadUrl } from "../utils/pictureUtils";
import { FileSharingPrompt } from "../files/FileSharingPrompt";
import { useAuth } from "../auth/AuthContext";
import { connectorsAPI } from "../api/connectorsApi";

type ImagePickerDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (data: { filename: string }) => void;
    uploadOptions?: {
        collection?: string;
        uid?: string;
        field?: string;
        userId?: string;
    };
    title?: string;
    /** When set, also loads images from this connector's remote upload registry. */
    connectorKey?: string;
    /** Base URL for resolving connector image thumbnails (e.g. https://veeninglab.com/rest). */
    connectorAssetPublicUrl?: string | null;
};

type ConnectorImageRecord = { filename: string; name: string; gallery: string | null };

/** Candidate URLs for previewing a locally uploaded image, tried in order until one loads. */
function imageUrlCandidates(name: string): string[] {
    const { src, thumb } = resolveLabwickImageUrls(name);
    const uploadsUrl = resolveLabwickUploadUrl(name);
    return [...new Set([thumb, src, uploadsUrl].filter(Boolean))];
}

function connectorImageUrl(filename: string, assetPublicUrl: string): string {
    const base = assetPublicUrl.replace(/\/+$/, "");
    return `${base}/img/${filename}`;
}

function ImageThumb({ name, className }: { name: string; className?: string }) {
    const candidates = useMemo(() => imageUrlCandidates(name), [name]);
    const [index, setIndex] = useState(0);

    useEffect(() => {
        setIndex(0);
    }, [name]);

    if (index >= candidates.length) {
        return (
            <div className={cn("flex items-center justify-center bg-muted text-muted-foreground", className)}>
                <ImageOff className="h-6 w-6" />
            </div>
        );
    }

    return (
        <img
            src={candidates[index]}
            alt={name}
            loading="lazy"
            className={cn("object-cover", className)}
            onError={() => setIndex((i) => i + 1)}
        />
    );
}

function ConnectorImageThumb({ filename, assetPublicUrl, className }: { filename: string; assetPublicUrl: string; className?: string }) {
    const src = connectorImageUrl(filename, assetPublicUrl);
    const [failed, setFailed] = useState(false);

    useEffect(() => { setFailed(false); }, [src]);

    if (failed) {
        return (
            <div className={cn("flex items-center justify-center bg-muted text-muted-foreground", className)}>
                <ImageOff className="h-6 w-6" />
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={filename}
            loading="lazy"
            className={cn("object-cover", className)}
            onError={() => setFailed(true)}
        />
    );
}

const ImagePickerDialog = ({ open, onOpenChange, onSelect, uploadOptions, title = "Choose image", connectorKey, connectorAssetPublicUrl }: ImagePickerDialogProps) => {
    const [images, setImages] = useState<ImageRecord[]>([]);
    const [connectorImages, setConnectorImages] = useState<ConnectorImageRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { user } = useAuth();
    const userInGroup = Array.isArray((user as any)?.groups) && (user as any).groups.length > 0;
    const [pendingShare, setPendingShare] = useState<{ fileId: string; filename: string } | null>(null);

    useEffect(() => {
        if (!open) return;
        setError("");
        setQuery("");
        setLoading(true);

        const localImagesPromise = connectorKey
            ? Promise.resolve({ success: true, images: [] as ImageRecord[] })
            : listImages().catch(() => null);

        const connectorImagesPromise = connectorKey
            ? connectorsAPI.images(connectorKey)
                .then((res) => res.images ?? [])
                .catch(() => [] as ConnectorImageRecord[])
            : Promise.resolve([] as ConnectorImageRecord[]);

        Promise.all([localImagesPromise, connectorImagesPromise])
            .then(([localData, remoteImages]) => {
                if (localData?.success && Array.isArray(localData.images)) {
                    setImages(localData.images);
                } else {
                    setImages([]);
                    if (localData && !localData.success) setError("Failed to load local images");
                }
                setConnectorImages(remoteImages);
            })
            .catch(() => setError("Failed to load images"))
            .finally(() => setLoading(false));
    }, [open, connectorKey]);

    const filteredLocal = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return images;
        return images.filter((img) => img.name.toLowerCase().includes(q));
    }, [images, query]);

    const filteredConnector = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return connectorImages;
        return connectorImages.filter((img) =>
            img.filename.toLowerCase().includes(q) || (img.name || "").toLowerCase().includes(q)
        );
    }, [connectorImages, query]);

    const totalCount = filteredLocal.length + filteredConnector.length;

    const handlePick = (name: string) => {
        onSelect({ filename: name });
        onOpenChange(false);
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setError("");
        setUploading(true);
        uploadFile(file, { ...readSharingDefaults(), ...uploadOptions })
            .then((data) => {
                if (data?.success && data.filename) {
                    if (userInGroup && data.fileId) {
                        setPendingShare({ fileId: data.fileId, filename: data.filename });
                    } else {
                        onSelect({ filename: data.filename });
                        onOpenChange(false);
                    }
                } else {
                    setError(data?.message || "Failed to upload image");
                }
            })
            .catch((err) => setError(err?.message || "Failed to upload image"))
            .finally(() => setUploading(false));
    };

    const finishPendingShare = () => {
        const share = pendingShare;
        setPendingShare(null);
        if (share) {
            onSelect({ filename: share.filename });
            onOpenChange(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        Upload a new image or pick one from earlier uploads.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search images..."
                            className="pl-8"
                        />
                    </div>
                    {!connectorKey && (
                        <>
                            <Button onClick={handleUploadClick} disabled={uploading}>
                                {uploading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Upload className="mr-2 h-4 w-4" />
                                )}
                                Upload new
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleFileChosen}
                            />
                        </>
                    )}
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <div className="max-h-[50vh] min-h-[12rem] overflow-y-auto">
                    {loading ? (
                        <div className="flex h-48 items-center justify-center text-muted-foreground">
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Loading images...
                        </div>
                    ) : totalCount === 0 ? (
                        <div className="flex h-48 flex-col items-center justify-center gap-1 text-muted-foreground">
                            <ImageOff className="h-8 w-8" />
                            <span className="text-sm">
                                {images.length === 0 && connectorImages.length === 0
                                    ? "No images available"
                                    : "No images match your search"}
                            </span>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredConnector.length > 0 && connectorAssetPublicUrl && (
                                <div>
                                    {filteredLocal.length > 0 && (
                                        <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                            From connector
                                        </p>
                                    )}
                                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                                        {filteredConnector.map((img) => (
                                            <button
                                                key={img.filename}
                                                type="button"
                                                onClick={() => handlePick(img.filename)}
                                                className="group flex flex-col gap-1 rounded-lg border p-1.5 text-left transition-colors hover:border-primary hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
                                                title={img.name || img.filename}
                                            >
                                                <ConnectorImageThumb
                                                    filename={img.filename}
                                                    assetPublicUrl={connectorAssetPublicUrl}
                                                    className="h-24 w-full rounded-md"
                                                />
                                                <span className="truncate text-xs text-muted-foreground group-hover:text-foreground">
                                                    {img.name || img.filename.split("/").pop()}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {filteredLocal.length > 0 && (
                                <div>
                                    {filteredConnector.length > 0 && (
                                        <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                            Local uploads
                                        </p>
                                    )}
                                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                                        {filteredLocal.map((img) => (
                                            <button
                                                key={img.name}
                                                type="button"
                                                onClick={() => handlePick(img.name)}
                                                className="group flex flex-col gap-1 rounded-lg border p-1.5 text-left transition-colors hover:border-primary hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
                                                title={img.name}
                                            >
                                                <ImageThumb name={img.name} className="h-24 w-full rounded-md" />
                                                <span className="truncate text-xs text-muted-foreground group-hover:text-foreground">
                                                    {img.name.split("/").pop()}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
            <FileSharingPrompt
                open={pendingShare !== null}
                fileId={pendingShare?.fileId ?? null}
                filename={pendingShare?.filename}
                onResolved={finishPendingShare}
            />
        </Dialog>
    );
};

export default ImagePickerDialog;
